import type { HelpGuide } from '../types';

export const assetCategoriesGuide: HelpGuide = {
  key: 'asset-categories',
  title: 'หมวดสินทรัพย์ (Asset Categories)',
  subtitle: 'เมนู: ระบบ ▸ หมวดสินทรัพย์',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/asset-categories/คู่มือการใช้งาน_หมวดสินทรัพย์_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “หมวดสินทรัพย์ (Asset Categories)” ใช้ตั้งค่ากลุ่มสินทรัพย์ถาวร (เช่น เครื่องจักร อาคาร ยานพาหนะ อุปกรณ์สำนักงาน) โดยแต่ละหมวดจะกำหนด “ค่า default” ของอายุการใช้งานและมูลค่าซาก พร้อมผูก “บัญชี GL” 5 ช่องไว้ล่วงหน้า เพื่อให้ระบบนำไปใช้ตอนลงทะเบียนสินทรัพย์ใหม่และตอนโพสต์ใบสำคัญ (JV) ค่าเสื่อมราคาและการจำหน่ายโดยอัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'แนวคิด: หมวดสินทรัพย์ = แม่แบบการลงบัญชี — เมื่อตั้งค่าหมวดให้ครบ (อายุการใช้งาน + มูลค่าซาก + บัญชี GL 5 ช่อง) แล้วลงทะเบียนสินทรัพย์เข้าหมวดนั้น ระบบจะคิดค่าเสื่อมและลง JV ให้ตามบัญชีที่ผูกไว้ ไม่ต้องระบุเลขบัญชีซ้ำทุกครั้ง',
        },
        { kind: 'para', text: 'สิ่งที่ทำได้ในหน้านี้:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ดูภาพรวมทุกหมวด — ',
              text: 'การ์ดสรุป + ตารางหมวดพร้อมบัญชี GL และค่า default',
            },
            { lead: 'ค้นหา/กรองหมวด — ', text: 'ค้นด้วยรหัสหรือชื่อหมวด' },
            {
              lead: 'เพิ่มหมวดใหม่ — ',
              text: 'กำหนดรหัส ชื่อ อายุการใช้งาน มูลค่าซาก และบัญชี GL',
            },
            { lead: 'แก้ไขหมวดเดิม — ', text: 'ปรับชื่อ ค่า default หรือบัญชี GL (รหัสแก้ไม่ได้)' },
            {
              lead: 'ปิดใช้งานหมวด — ',
              text: 'ซ่อนหมวดที่เลิกใช้จากตัวเลือกตอนลงทะเบียนสินทรัพย์',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/asset-categories/01-overview.png',
          caption:
            'ภาพรวมหมวดสินทรัพย์ — การ์ดสรุป (หมวดทั้งหมด / กำหนด GL ครบ / คำแนะนำ) และตารางหมวดพร้อมอายุการใช้งาน มูลค่าซาก และบัญชี GL',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปด้านบน 3 ใบ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'หมวดทั้งหมด — ',
              text: 'จำนวนหมวดทั้งหมด พร้อมแยกว่ามีกี่หมวด “ใช้งาน” และกี่หมวด “ปิด”',
            },
            {
              lead: 'กำหนด GL ครบ — ',
              text: 'จำนวนหมวดที่กำหนดบัญชี GL ครบทั้ง cost / accDep / depExp / gain / loss',
            },
            {
              lead: 'คำแนะนำ — ',
              text: 'ข้อความแนะนำ “แก้ defaultLifeMonths + defaultSalvage + บัญชี GL 5 ช่องต่อหมวด ระบบจะใช้ค่านี้เป็น default ตอนลงทะเบียนสินทรัพย์ใหม่” (defaultLifeMonths = อายุการใช้งานเป็นเดือน, defaultSalvage = มูลค่าซาก)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มมุมขวาบน: ส่งออก (สำหรับดาวน์โหลด — สงวนไว้ใช้ในอนาคต) และ เพิ่มหมวด (ปุ่มหลักสำหรับสร้างหมวดใหม่ ต้องเลือกบริษัทก่อนจึงกดได้)',
        },
        {
          kind: 'para',
          text: 'ตารางหมวดสินทรัพย์มีคอลัมน์: รหัส · ชื่อหมวด (ไทยพร้อมชื่ออังกฤษกำกับ) · อายุ (เดือน) (แสดงเป็นปีกำกับ) · ซาก (฿) · Dr Cost · Cr AccDep · Dr DepExp · Cr Gain · Dr Loss · สถานะ · จัดการ มุมขวาของตารางแสดงตัวนับผลลัพธ์ “แสดง X จาก Y หมวด”',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'บัญชี GL 5 ช่องในตารางคือบัญชีที่ระบบใช้ลง JV ของสินทรัพย์ในหมวดนั้น: Dr Cost = ราคาทุน, Cr AccDep = ค่าเสื่อมสะสม, Dr DepExp = ค่าเสื่อมราคา, Cr Gain = กำไรจากจำหน่าย, Dr Loss = ขาดทุนจากจำหน่าย',
        },
      ],
    },
    {
      id: 'search',
      heading: '3. การค้นหาและกรองหมวด',
      blocks: [
        {
          kind: 'para',
          text: 'พิมพ์คำค้นในช่อง “ค้นหา รหัส / ชื่อหมวด” ระบบจะกรองตารางทันทีโดยจับคู่ทั้งรหัสและชื่อหมวด (ภาษาไทย) เช่น พิมพ์ “MAC” หรือชื่อหมวดบางส่วน ตัวนับมุมขวาจะแสดง “แสดง X จาก Y หมวด” ให้ทราบว่าเหลือกี่รายการ',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'ถ้าค้นแล้วไม่พบรายการ ตารางจะแสดง “ไม่มีข้อมูล” — ลบคำค้นเพื่อกลับมาดูทุกหมวด ตารางนี้แสดงทั้งหมวดที่ใช้งานและที่ปิดอยู่',
        },
        {
          kind: 'image',
          src: '/help/asset-categories/02-search-filter.png',
          caption: 'ค้นหา/กรองหมวดด้วยรหัสหรือชื่อหมวด พร้อมตัวนับผลลัพธ์ “แสดง X จาก Y หมวด”',
        },
      ],
    },
    {
      id: 'add',
      heading: '4. การเพิ่มหมวดสินทรัพย์ใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม เพิ่มหมวด ที่มุมขวาบน ระบบจะเปิดหน้าต่าง “เพิ่มหมวดสินทรัพย์ใหม่” ซึ่งแบ่งฟอร์มเป็น 4 ส่วน:',
        },
        {
          kind: 'steps',
          items: [
            {
              title: 'ข้อมูลหมวด',
              text: 'กรอก รหัส * (ตัวพิมพ์ใหญ่ เช่น MACHINE) · ชื่อหมวด (ไทย) * (เช่น เครื่องจักรการผลิต) · ชื่อหมวด (อังกฤษ) (ไม่บังคับ เช่น Machinery)',
            },
            {
              title: 'ค่า default สำหรับสินทรัพย์ในหมวดนี้',
              text: 'กรอก อายุการใช้งาน (เดือน) * (1–600 เดือน ระบบแสดง ≈ N ปีกำกับ) · มูลค่าซาก (บาท) (ไม่ติดลบ — แนวปฏิบัติไทยมักใส่ ฿1 เพื่อรักษาสินทรัพย์ในบัญชี)',
            },
            {
              title: 'บัญชี GL ที่ใช้ใน JV',
              text: 'ใส่รหัสบัญชีจากผังบัญชีให้ครบ 5 ช่อง: Dr Cost — ราคาทุน *, Cr AccDep — ค่าเสื่อมสะสม *, Dr DepExpense — ค่าเสื่อมราคา *, Cr Gain — กำไรจากจำหน่าย *, Dr Loss — ขาดทุนจากจำหน่าย *',
            },
            {
              title: 'สถานะ',
              text: 'ติ๊ก “ใช้งาน (แสดงในตัวเลือกตอนลงทะเบียนสินทรัพย์)” เพื่อให้หมวดนี้ปรากฏตอนลงทะเบียนสินทรัพย์ (ค่าเริ่มต้นติ๊กไว้)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'เมื่อกรอกครบกดปุ่ม + เพิ่มหมวด ระหว่างบันทึกปุ่มจะแสดง “กำลังบันทึก…” กดปุ่ม ยกเลิก เพื่อปิดหน้าต่างโดยไม่บันทึก',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'ช่องที่มีเครื่องหมาย * เป็นช่องบังคับ โดยเฉพาะบัญชี GL ทั้ง 5 ช่องต้องกรอกให้ครบ มิฉะนั้นระบบจะคิดค่าเสื่อม/ลง JV ของสินทรัพย์ในหมวดนี้ไม่ได้',
        },
        {
          kind: 'image',
          src: '/help/asset-categories/03-add-modal-empty.png',
          caption:
            'หน้าต่างเพิ่มหมวดสินทรัพย์ใหม่ — 4 ส่วน: ข้อมูลหมวด, ค่า default, บัญชี GL 5 ช่อง และสถานะ',
        },
      ],
    },
    {
      id: 'fill',
      heading: '5. ตัวอย่างการกรอกข้อมูลหมวดใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'ตัวอย่างการตั้งหมวด “เครื่องจักรการผลิต”: กรอกรหัส MACHINE · ชื่อหมวด (ไทย) เครื่องจักรการผลิต · ชื่อหมวด (อังกฤษ) Machinery จากนั้นกำหนดอายุการใช้งานและมูลค่าซากตามนโยบายของบริษัท แล้วใส่รหัสบัญชี GL ทั้ง 5 ช่องจากผังบัญชี',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'รหัสหมวดควรตั้งให้สื่อความหมายและไม่ซ้ำ เพราะหลังบันทึกแล้วจะแก้ไม่ได้ (รหัสล็อกถาวร) ส่วนชื่อไทย/อังกฤษ ค่า default และบัญชี GL ยังแก้ไขได้ภายหลัง',
        },
        {
          kind: 'image',
          src: '/help/asset-categories/04-add-modal-filled.png',
          caption: 'ตัวอย่างกรอกข้อมูลหมวดใหม่ — รหัส ชื่อไทย ชื่ออังกฤษ และค่าตั้งต้น',
        },
      ],
    },
    {
      id: 'edit',
      heading: '6. การแก้ไขหมวด',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกไอคอนรูปดวงตาที่แถวของหมวด ระบบจะเปิดหน้าต่าง “แก้ไขหมวด [รหัส]” ด้วยฟอร์มเดียวกับตอนเพิ่ม แต่ดึงค่าเดิมมาแสดงครบทุกช่อง แก้ไขชื่อ ค่า default หรือบัญชี GL ได้ตามต้องการ แล้วกด บันทึก',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'ช่องรหัสจะถูกล็อกในโหมดแก้ไข (แสดงข้อความ “รหัสแก้ไม่ได้”) เพราะรหัสถูกอ้างอิงโดยสินทรัพย์ที่ลงทะเบียนไว้แล้ว หากต้องการเปลี่ยนรหัสต้องสร้างหมวดใหม่และปิดหมวดเดิมแทน',
        },
        {
          kind: 'image',
          src: '/help/asset-categories/05-edit-modal.png',
          caption: 'หน้าต่างแก้ไขหมวด — รหัสถูกล็อก (รหัสแก้ไม่ได้) พร้อมค่าบัญชี GL เดิม',
        },
      ],
    },
    {
      id: 'status',
      heading: '7. สถานะหมวดและการปิดใช้งาน',
      blocks: [
        {
          kind: 'para',
          text: 'แต่ละหมวดมีสถานะแสดงเป็นป้าย: “ใช้งาน” (จุดเขียว) หรือ “ปิด” (จุดเทา) หมวดที่ “ใช้งาน” เท่านั้นจึงจะปรากฏให้เลือกตอนลงทะเบียนสินทรัพย์ใหม่',
        },
        {
          kind: 'para',
          text: 'การปิดใช้งานทำได้โดยคลิกไอคอน x ที่แถว ระบบจะถามยืนยัน “ปิดใช้งานหมวด [รหัส]?” เมื่อยืนยันแล้วหมวดจะถูกตั้งเป็น “ปิด” (เป็นการปิดแบบนิ่ม ไม่ลบทิ้ง — สินทรัพย์เดิมในหมวดยังอยู่และคำนวณค่าเสื่อมต่อไปได้) ปุ่มปิดจะกดไม่ได้ถ้าหมวดถูกปิดอยู่แล้ว',
        },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'หมวดที่เปิดใช้ — แสดงเป็นตัวเลือกตอนลงทะเบียนสินทรัพย์ใหม่ (จุดเขียว)'],
            ['ปิด', 'หมวดที่ปิดใช้งานแล้ว — ไม่แสดงในตัวเลือก แต่ข้อมูลเดิมยังคงอยู่ (จุดเทา)'],
          ],
        },
        {
          kind: 'image',
          src: '/help/asset-categories/06-status-badges.png',
          caption: 'ป้ายสถานะ “ใช้งาน” (เขียว) / “ปิด” (เทา) และปุ่มปิดใช้งานที่คอลัมน์จัดการ',
        },
      ],
    },
    {
      id: 'faq',
      heading: '8. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมปุ่ม “เพิ่มหมวด” กดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ต้องเลือกบริษัทก่อน และต้องมีสิทธิ์จัดการหมวดสินทรัพย์ (assetcategory.manage)',
            },
          ],
        },
        { kind: 'para', text: 'แก้รหัสหมวดที่ตั้งผิดได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'แก้ไม่ได้ — รหัสล็อกถาวรหลังบันทึก ให้สร้างหมวดใหม่ด้วยรหัสที่ถูกต้องแล้วปิดใช้งานหมวดเดิม',
            },
          ],
        },
        { kind: 'para', text: 'ปิดหมวดไปแล้ว สินทรัพย์เดิมหายไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่หาย — เป็นการปิดแบบนิ่ม (soft-delete) สินทรัพย์เดิมยังอยู่ เพียงแต่หมวดจะไม่ปรากฏให้เลือกตอนลงทะเบียนสินทรัพย์ใหม่',
            },
          ],
        },
        { kind: 'para', text: 'ต้องกรอกบัญชี GL ครบทุกช่องไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ควรกรอกครบทั้ง 5 ช่อง (cost / accDep / depExp / gain / loss) เพื่อให้ระบบลง JV ค่าเสื่อมและการจำหน่ายได้ครบถ้วน การ์ด “กำหนด GL ครบ” ช่วยตรวจว่าหมวดใดยังตั้งไม่ครบ',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '9. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'หมวดสินทรัพย์ — ',
              text: 'กลุ่มสินทรัพย์ถาวรที่ใช้ค่า default และบัญชี GL ร่วมกัน (เช่น เครื่องจักร อาคาร ยานพาหนะ)',
            },
            {
              lead: 'อายุการใช้งาน (เดือน) — ',
              text: 'จำนวนเดือนที่ใช้คิดค่าเสื่อมราคาเริ่มต้นของสินทรัพย์ในหมวด (1–600 เดือน)',
            },
            {
              lead: 'มูลค่าซาก — ',
              text: 'มูลค่าคงเหลือที่ไม่นำมาคิดค่าเสื่อม แนวปฏิบัติไทยมักตั้งไว้ ฿1',
            },
            {
              lead: 'Dr Cost — ',
              text: 'บัญชีราคาทุนของสินทรัพย์ (เดบิตตอนได้มา / เครดิตตอนจำหน่าย)',
            },
            {
              lead: 'Cr AccDep — ',
              text: 'บัญชีค่าเสื่อมราคาสะสม (เครดิตตอนคิดค่าเสื่อม / เดบิตตอนจำหน่าย)',
            },
            { lead: 'Dr DepExp — ', text: 'บัญชีค่าเสื่อมราคา (เดบิตทุกงวดที่คิดค่าเสื่อม)' },
            { lead: 'Cr Gain — ', text: 'บัญชีกำไรจากการจำหน่ายสินทรัพย์' },
            { lead: 'Dr Loss — ', text: 'บัญชีขาดทุนจากการจำหน่ายสินทรัพย์' },
            {
              lead: 'ปิดแบบนิ่ม (soft-delete) — ',
              text: 'การปิดใช้งานโดยไม่ลบข้อมูล (ตั้งสถานะเป็น “ปิด”) ข้อมูลเดิมยังคงอยู่',
            },
          ],
        },
      ],
    },
  ],
};
